import dayjs from 'dayjs';
import html3pdf from 'html3pdf';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Descendant } from 'slate';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { fetchImageFromS3AndConvertToBase64 } from '../../../utils/s3';
import { regenerateIcon } from '../../../assets/hostedassets';
import { TIME_DATE_FORMAT } from '../../../utils/constants/commonConstants';
import { closeCommentsThread, resetAndHideCommentsThread } from '../../../store/commentsThread/commentsThread_actions';
import { convertToSnakeCase } from '../../../utils/utils';
import { Button, Modal } from '../../_custom';
import LoadingSpinner from '../../_custom/LoadingSpinner/LoadingSpinner';
import TextEditor from '../../TextEditor/TextEditor';
import styles from './BureauKycConsentEditor.module.scss';
import { TEXT_EDITOR_TYPES } from '../../../store/investor/constants';

interface BureauKycConsentEditorProps {
    source: string;
    openModal: boolean;
    orgId: string;
    orgName: string;
    generating: boolean;
    editorInitialData: any;
    editorReady: boolean;
    savingData: boolean;
    saveBureauKycConsent: (content: any) => void;
    setShowRegenConfirmModal: (show: boolean) => void;
    userInfo: any;
}

function BureauKycConsentEditor({
    source,
    openModal,
    orgId,
    orgName,
    generating,
    editorInitialData,
    editorReady,
    savingData,
    saveBureauKycConsent,
    setShowRegenConfirmModal,
    userInfo,
}: BureauKycConsentEditorProps) {
    const dispatch = useAppDispatch();
    const [saving, setSaving] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const contentToPrint = useRef(null);
    const editorContent = useRef<Descendant[]>([]);
    const domainMetadata = useAppSelector((state) => state.user.domainMetadata);
    const mainImageUrl = domainMetadata?.mainImageUrl;
    const [triggerSave, setTriggerSave] = useState(false);
    const user = useAppSelector((state) => state.user.userData);

    useEffect(() => {
        dispatch(resetAndHideCommentsThread());
    }, []);

    useEffect(() => {
        if (triggerSave) {
            setTriggerSave(false);
        }
    }, [triggerSave, editorContent, orgId]);

    const getHTMLElementForExport = async () => {
        let clonedElement: any;
        try {
            const el = document?.getElementById('element-to-be-downloaded-as-pdf');

            clonedElement = el?.cloneNode(true);

            if (mainImageUrl) {
                const response: any = await fetchImageFromS3AndConvertToBase64(mainImageUrl);

                if (response) {
                    const topDiv = document.createElement('div');

                    const topImg = document.createElement('img');

                    topDiv.style.display = 'flex';
                    topDiv.style.justifyContent = 'flex-start';
                    topDiv.style.marginBottom = '48px';

                    topImg.src = response;
                    topImg.alt = '';
                    topImg.style.height = '30px';
                    topImg.crossOrigin = 'anonymous';
                    topImg.style.transform = 'translateY(40px)';
                    topDiv.appendChild(topImg);

                    clonedElement.insertBefore(topDiv, clonedElement.firstChild);
                }
            }
        } catch (err) {
            console.error('error', err);
        }
        return clonedElement;
    };

    const removePageBreaks = () => {
        const elements = document.querySelectorAll('#page-break-line');
        elements.forEach((hr, index) => {
            const hrElement = hr as HTMLElement;
            hrElement.style.visibility = 'hidden';
        });
    };

    const showPageBreaks = () => {
        const elements = document.querySelectorAll('#page-break-line');
        elements.forEach((hr, index) => {
            const hrElement = hr as HTMLElement;
            hrElement.style.visibility = 'visible';
        });
    };

    const handlePreview = useReactToPrint({
        onPrintError: (error) => console.error(error),
        content: () => contentToPrint.current,
        documentTitle: 'Print This Document',
        removeAfterPrint: true,
        print: async (printIframe: HTMLIFrameElement) => {
            removePageBreaks();
            const getElementForExport = await getHTMLElementForExport();

            try {
                const document = printIframe.contentDocument;
                if (document) {
                    var opt = {
                        padding: 20,
                        margin: 5,
                        filename: `BKC_${convertToSnakeCase(orgName)}.pdf`,
                        html2canvas: { scale: 2, useCORS: true, dpi: 300, allowTaint: false },
                        jsPDF: { unit: 'mm', format: 'a3', orientation: 'portrait' },
                    };
                    html3pdf()
                        .from(getElementForExport)
                        .set(opt)
                        .toPdf()
                        .output('bloburl')
                        .then((bloburl) => {
                            window.open(bloburl, '_blank');
                            setLoadingPreview(false);
                        })
                        .catch(() => setLoadingPreview(false));
                }
            } catch (err) {
                console.error('Error in generating preview', err);
            } finally {
                showPageBreaks();
            }
        },
    });

    const handleClose = () => {
        dispatch(closeCommentsThread());
        setTriggerSave(true);
        saveBureauKycConsent(editorContent.current);
    };

    const userMessage = useMemo(() => {
        if (!userInfo?.userName || !userInfo?.onePagerData?.lastUpdatedAt) {
            return '';
        }
        const formattedDate = dayjs(userInfo.onePagerData.lastUpdatedAt).format(TIME_DATE_FORMAT);

        return `Last edited on ${formattedDate} by ${userInfo.userName}`;
    }, [userInfo]);

    const handlePreviewClick = (e) => {
        e.preventDefault();
        setLoadingPreview(true);
        handlePreview(null, () => contentToPrint?.current);
    };

    const handleSaveAndClose = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            setTriggerSave(true);
            dispatch(closeCommentsThread());
            saveBureauKycConsent(editorContent.current);
            await downloadPDF(orgName);
        } catch (err) {
            console.error('Error in saving and closing:', err);
        } finally {
            setSaving(false);
        }
    };

    const downloadPDF = async (companyName) => {
        removePageBreaks();
        const getElementForExport = await getHTMLElementForExport();

        try {
            const currentOrgName = companyName || orgName || 'Company';
            const finalFileName = currentOrgName.replace(/[^a-zA-Z0-9]/g, '_');
            var opt = {
                padding: 20,
                margin: 5,
                html2canvas: { scale: 2, useCORS: true, dpi: 300, allowTaint: false },
                jsPDF: { unit: 'mm', format: 'a3', orientation: 'portrait' },
            };

            // Use save method for actual download with correct filename
            await html3pdf().from(getElementForExport).set(opt).save(`Bureau_KYC_Consent_${finalFileName}.pdf`);

            setSaving(false);
        } catch (err) {
            console.error('Error in generating PDF', err);
            setSaving(false);
        } finally {
            showPageBreaks();
        }
    };

    // Add loading state UI for regeneration
    const renderRegenerateButton = () => {
        return (
            <div
                className={styles.Regen}
                onClick={() => {
                    !generating && setShowRegenConfirmModal(true);
                }}
            >
                {generating ? (
                    <>
                        <LoadingSpinner color="var(--primary-text-colour)" height="18px" style={{ margin: 0 }} />
                        <span>Regenerating</span>
                    </>
                ) : (
                    <>
                        <img src={regenerateIcon} alt={'re'} height={'24px'} />
                        <span>Regenerate</span>
                    </>
                )}
            </div>
        );
    };

    return openModal ? (
        <Modal
            size="full"
            open={openModal}
            style={{ padding: 0, fontSize: '12px', lineHeight: '140%', position: 'absolute' }}
            persistent
            withoutCloseButton
        >
            <div className={styles.Container}>
                <div className={styles.Top}>
                    {!!editorReady && (
                        <TextEditor
                            source={source}
                            editorInitialData={editorInitialData}
                            targetRef={contentToPrint}
                            editorContentRef={editorContent}
                            assesseeOrgId={orgId}
                            generating={generating}
                            onClose={handleClose}
                            savingData={savingData}
                            editorType={TEXT_EDITOR_TYPES.BUREAU_KYC_CONSENT}
                        />
                    )}
                    <div className={styles.Footer}>
                        <div>{renderRegenerateButton()}</div>

                        <div className={styles.Text}>*Right-click tables to modify: add rows, add columns, or delete</div>

                        {userMessage && <div className={styles.Text}>{userMessage}</div>}

                        <div className={styles.Buttons}>
                            <Button
                                text="Preview"
                                variant="purpleinverted"
                                onClick={handlePreviewClick}
                                style={{ margin: '0 20px 0 0 ' }}
                                loading={loadingPreview}
                            />
                            <Button
                                text="Save & Download"
                                variant="primary"
                                onClick={handleSaveAndClose}
                                style={{ marginTop: '0' }}
                                loading={saving}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.Comments} id="bkc-comment-container">
                    {/* //Container Element Where comments thread is rendered via portal */}
                </div>
            </div>
        </Modal>
    ) : (
        <></>
    );
}

export default BureauKycConsentEditor;
